import { useMemo, useEffect } from 'react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { HeartPulse, Brain } from 'lucide-react';
import { SessionDto } from '@repo/shared/dtos/devices/SessionDto';
import { MetricTimeSeries } from '@repo/shared/dtos/devices/SessionPpgDto';
import { AnalyticsModal } from '@repo/ui/components/Modal/AnalyticsModal';
import { AnalyticsTabs } from '@repo/ui/components/AnalyticsTabs';
import { Spinner } from '@repo/ui/components/Spinner';
import { ChartErrorBoundary } from '../../../components/common/ChartErrorBoundary';
import { formatDate, formatTime } from '../../../utils/dateFormatters';
import { useSessionPpg } from '../api/useSessionPpg';
import { useSessionEeg } from '../api/useSessionEeg';
import { useSessionDetailState } from '../hooks/useSessionDetailState';
import { MetricTogglePills } from './MetricTogglePills';
import { MultiAxisChart } from './MultiAxisChart';
import {
  METRIC_CONFIGS,
  EEG_METRIC_CONFIGS,
  MetricType,
  EegMetricType,
  CHART_HEIGHT,
} from '../config/chartConfig';
import { palette } from '../../../components/analytics/shared/theme';

interface SessionInsightsModalProps {
  session: SessionDto;
  onClose: () => void;
  defaultView?: 'heart' | 'brain';
}

const LoadingView = ({ message }: { message: string }) => (
  <div className="flex h-40 items-center justify-center gap-3">
    <Spinner size="lg" />
    <p className="text-sm text-[var(--anl-text-muted)]">{message}</p>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-lg bg-[var(--anl-surface-elev)] p-8 text-center text-sm text-[var(--anl-text-muted)]">
    {message}
  </div>
);

const FetchError = ({ message }: { message: string }) => (
  <div className="rounded-lg bg-[var(--anl-surface-elev)] p-6 text-center text-sm text-[var(--anl-error)]">
    {message}
  </div>
);

const MODAL_TABS = [
  { id: 'heart', label: 'Heart', icon: <HeartPulse className="h-4 w-4" /> },
  { id: 'brain', label: 'Brain', icon: <Brain className="h-4 w-4" /> },
] as const;

export const SessionInsightsModal = ({
  session,
  onClose,
  defaultView = 'heart',
}: SessionInsightsModalProps) => {
  const isMobile = useIsMobile();
  const { ppgData, isLoading: isPpgLoading, error: ppgError } = useSessionPpg(session.expsessionid);
  const { eegData, isLoading: isEegLoading, error: eegError } = useSessionEeg(session.expsessionid);

  const {
    activeView,
    selectedHeartMetrics,
    selectedBrainMetrics,
    setActiveView,
    setHeartMetrics,
    setBrainMetrics,
  } = useSessionDetailState(defaultView);

  const availableHeartMetrics = useMemo((): MetricType[] => {
    const m: MetricType[] = [];
    if (ppgData?.hrMetrics) m.push('hr');
    if (ppgData?.hrvMetrics) m.push('hrv');
    if (ppgData?.rrMetrics) m.push('rr');
    return m;
  }, [ppgData]);

  const availableBrainMetrics = useMemo((): EegMetricType[] => {
    const m: EegMetricType[] = [];
    if (eegData?.focusMetrics) m.push('focus');
    if (eegData?.calmAlertMetrics) m.push('calmAlert');
    return m;
  }, [eegData]);

  // Sync heart selection to available metrics when data arrives
  useEffect(() => {
    if (availableHeartMetrics.length === 0) return;
    const valid = selectedHeartMetrics.filter(m => availableHeartMetrics.includes(m as MetricType));
    if (valid.length === 0) {
      setHeartMetrics([availableHeartMetrics[0]]);
    } else if (valid.length !== selectedHeartMetrics.length) {
      setHeartMetrics(valid as MetricType[]);
    }
  }, [availableHeartMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync brain selection to available metrics when data arrives
  useEffect(() => {
    if (availableBrainMetrics.length === 0) return;
    const valid = selectedBrainMetrics.filter(m =>
      availableBrainMetrics.includes(m as EegMetricType),
    );
    if (valid.length === 0) {
      setBrainMetrics([availableBrainMetrics[0]]);
    } else if (valid.length !== selectedBrainMetrics.length) {
      setBrainMetrics(valid as EegMetricType[]);
    }
  }, [availableBrainMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  const heartMetricData = useMemo((): Record<string, MetricTimeSeries | null> => {
    if (!ppgData) return {};
    return { hr: ppgData.hrMetrics, hrv: ppgData.hrvMetrics, rr: ppgData.rrMetrics };
  }, [ppgData]);

  const brainMetricData = useMemo((): Record<string, MetricTimeSeries | null> => {
    if (!eegData) return {};
    return {
      focus: eegData.focusMetrics,
      calmAlert: eegData.calmAlertMetrics,
    };
  }, [eegData]);

  const hasPpg = availableHeartMetrics.length > 0;
  const hasEeg = availableBrainMetrics.length > 0;

  const formattedDate = formatDate(session.createdts);
  const startTime = ppgData?.startTime || eegData?.startTime;
  const unitLabel = session.unitName || (session.unitid ? `Unit ${session.unitid}` : '');

  return (
    <AnalyticsModal
      isOpen={true}
      onClose={onClose}
      title={session.experienceName}
      headerContent={
        <AnalyticsTabs
          tabs={[...MODAL_TABS]}
          activeTab={activeView}
          onTabChange={v => setActiveView(v as 'heart' | 'brain')}
        />
      }
      className="mx-4 w-full max-w-7xl"
    >
      <div className="space-y-1 border-b border-[var(--anl-border)] pb-3 text-sm text-[var(--anl-text-muted)]">
        <span>{formattedDate}</span>
        {startTime && <span className="ml-3">{formatTime(startTime)}</span>}
        <p className="text-xs" style={{ color: palette.yellow }}>
          {unitLabel}
        </p>
        {session.experienceDescription && (
          <p className="text-xs">{session.experienceDescription}</p>
        )}
      </div>

      <div className="mt-4 space-y-4">
        {activeView === 'heart' && (
          <>
            {isPpgLoading ? (
              <LoadingView message="Loading heart data..." />
            ) : ppgError ? (
              <FetchError message="Heart data could not be loaded. Please try again." />
            ) : hasPpg ? (
              <>
                <MetricTogglePills
                  availableMetrics={availableHeartMetrics}
                  selectedMetrics={selectedHeartMetrics}
                  onMetricsChange={m => setHeartMetrics(m as MetricType[])}
                  metricConfigs={METRIC_CONFIGS}
                />
                <ChartErrorBoundary>
                  <MultiAxisChart
                    selectedMetrics={selectedHeartMetrics}
                    metricData={heartMetricData}
                    metricConfigs={METRIC_CONFIGS}
                    height={isMobile ? 300 : CHART_HEIGHT}
                    minSlotPx={isMobile ? 100 : 150}
                    startTime={ppgData?.startTime || eegData?.startTime || undefined}
                  />
                </ChartErrorBoundary>
              </>
            ) : (
              <EmptyState message="No heart rate data recorded for this session." />
            )}
          </>
        )}

        {activeView === 'brain' && (
          <>
            {isEegLoading ? (
              <LoadingView message="Loading brain data..." />
            ) : eegError ? (
              <FetchError message="Brain data could not be loaded. Please try again." />
            ) : hasEeg ? (
              <>
                <MetricTogglePills
                  availableMetrics={availableBrainMetrics}
                  selectedMetrics={selectedBrainMetrics}
                  onMetricsChange={m => setBrainMetrics(m as EegMetricType[])}
                  metricConfigs={EEG_METRIC_CONFIGS}
                />
                <ChartErrorBoundary>
                  <MultiAxisChart
                    selectedMetrics={selectedBrainMetrics}
                    metricData={brainMetricData}
                    metricConfigs={EEG_METRIC_CONFIGS}
                    height={isMobile ? 300 : CHART_HEIGHT}
                    minSlotPx={isMobile ? 100 : 150}
                    startTime={ppgData?.startTime || eegData?.startTime || undefined}
                  />
                </ChartErrorBoundary>
              </>
            ) : (
              <EmptyState message="No EEG data recorded for this session." />
            )}
          </>
        )}
      </div>
    </AnalyticsModal>
  );
};
